import { useCallback, useEffect, useState } from "react";

import { useAgencyDetails } from "@bokio/hooks/useAgencyDetails/useAgencyDetails";
import { AgencyUserPreferenceType } from "@bokio/hooks/useUserPreference/useAgencyUserPreference";
import { UserPreferenceType, useUserPreferenceWithoutCompany } from "@bokio/hooks/useUserPreference/useUserPreference";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useLazyApi } from "@bokio/mobile-web-shared/hooks/useApi/useApi";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import { useUmbracoApi } from "@bokio/shared/services/api/UmbracoApi";

import { useTopLevelUser } from "../TopLevelUserContext/useTopLevelUser";
import { ReleaseNotesContext } from "./ReleaseNotesContext";

import type { ReleaseNoteType } from "./ReleaseNotesContext";
import type { UmbracoReleaseNoteModel } from "@bokio/shared/models/UmbracoReleaseNotesModel";
import type { FC, PropsWithChildren } from "react";

type AgencyUserPreferencesDto = m.Bokio.Common.Contract.Services.UserPreferences.AgencyUserPreferencesDto;

interface whatsNewResult {
	hasNewNotes: boolean;
	shouldSetLastRead: boolean;
	latestWhatsNewDate: Date | undefined;
	noOfUpdates: number;
}

export const getWhatsNewInfo = (
	releaseNotes: UmbracoReleaseNoteModel[],
	lastReadWhatsNewDate: Date | undefined,
	isLoadingLatestRead?: boolean,
): whatsNewResult => {
	const firstPossibleNewsBadgeDate = new Date(2022, 3, 22);
	const newestReleaseNotePublished = () => {
		const date = releaseNotes.length ? new Date(releaseNotes[0].Published) : firstPossibleNewsBadgeDate;
		date.setMilliseconds(0);
		return date;
	};

	// lastRead defaults to firstPossibleNewsBadgeDate if nothing is read.
	// AM 2024-10-30 When loading default to today's date to make sure it doesn't look like there are unread release notes
	const lastRead = isLoadingLatestRead ? m.Day.today() : (lastReadWhatsNewDate ?? firstPossibleNewsBadgeDate);
	const getNoOfUpdates = releaseNotes.filter(r => new Date(r.Published) > lastRead).length;
	const getHasNewNotes = lastRead < newestReleaseNotePublished();
	const latestWhatsNewDateFromApi = releaseNotes.length > 0 ? new Date(releaseNotes[0].Published) : undefined;
	const getShouldSetLastRead = latestWhatsNewDateFromApi ? lastRead < latestWhatsNewDateFromApi : true;
	return {
		noOfUpdates: getNoOfUpdates,
		hasNewNotes: getHasNewNotes,
		shouldSetLastRead: getShouldSetLastRead,
		latestWhatsNewDate: latestWhatsNewDateFromApi,
	};
};

const ReleaseNotesContextProviderLoggedIn: FC<PropsWithChildren> = ({ children }) => {
	const [whatsNewDataCompany, setWhatsNewDataCompany] = useState<UmbracoReleaseNoteModel[]>([]);
	const [whatsNewDataAgency, setWhatsNewDataAgency] = useState<UmbracoReleaseNoteModel[]>([]);

	const [hasNewNotesAgency, setHasNewNotesAgency] = useState<boolean>(false);
	const [hasNewNotesCompany, setHasNewNotesCompany] = useState<boolean>(false);
	const [noOfUpdatesAgency, setNoOfUpdatesAgency] = useState<number>(0);

	const { getWhatsNew } = useUmbracoApi();

	const { agencyId: agencyIdFromHook, isAgency: isAgencyFromHook } = useAgencyDetails();

	// AM 2024-03-21 In select company page there is no agencyId from the hook above.
	// We look at the memberships for the user to get an agencyId.
	// We are interested in getting any one agency connected to the user, not all.
	// AM 2024-09-19 If the user is in a client company and clicking what's new it should not be from an agency perspective
	// but rather from a company perspective
	const { memberships } = useTopLevelUser();
	const agencyIdsFromMemberships = memberships?.partners.map(x => x.PartnerId);
	const agencyIdFromMemberships =
		agencyIdsFromMemberships?.length !== undefined && agencyIdsFromMemberships.length > 0
			? agencyIdsFromMemberships[0]
			: undefined;

	const { companyInfo } = useCompanyInfo();
	const agencyId = agencyIdFromHook ?? agencyIdFromMemberships;
	const isAgency = isAgencyFromHook || (companyInfo === undefined && agencyIdFromMemberships !== undefined);

	const { setPreference: setPreferenceWithoutCompany, getPreferenceRequest: getPreferenceWithoutCompanyRequest } =
		useUserPreferenceWithoutCompany(UserPreferenceType.LastReadWhatsNewDate);

	const [agencyUserGetPreference, getPreferenceRequest] = useLazyApi(
		proxy.BackOffice.AgencyUserPreferencesController.GetPreference.Get,
	);

	useEffect(() => {
		const { hasNewNotes, noOfUpdates } = getWhatsNewInfo(
			whatsNewDataAgency,
			getPreferenceRequest.data?.Data?.LastReadWhatsNewDateAgencyPreference?.Value,
			getPreferenceRequest.data?.Data === undefined,
		);
		// eslint-disable-next-line react-hooks/set-state-in-effect -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
		setHasNewNotesAgency(hasNewNotes);
		setNoOfUpdatesAgency(noOfUpdates);
	}, [
		whatsNewDataAgency,
		getPreferenceRequest.data?.Data?.LastReadWhatsNewDateAgencyPreference?.Value,
		getPreferenceRequest.data?.Data,
	]);

	const getAgencyUserPreferences = useCallback(() => {
		agencyId && agencyUserGetPreference(agencyId, AgencyUserPreferenceType.LastReadWhatsNewDateAgency);
	}, [agencyId, agencyUserGetPreference]);

	const [setPreference] = useLazyApi(proxy.BackOffice.AgencyUserPreferencesController.SetPreference.Post, {
		onSuccess: getAgencyUserPreferences,
	});

	const setUserPreference = useCallback(async () => {
		const type: ReleaseNoteType = isAgency ? "Agency" : "Company";

		const lastReadDate =
			type === "Agency"
				? getPreferenceRequest.data?.Data?.LastReadWhatsNewDateAgencyPreference?.Value
				: getPreferenceWithoutCompanyRequest.data?.Data?.LastReadWhatsNewDatePreference?.Value;

		const isLoading =
			type === "Agency"
				? getPreferenceRequest.data?.Data === undefined
				: getPreferenceWithoutCompanyRequest.data?.Data === undefined;

		const { shouldSetLastRead, latestWhatsNewDate } = getWhatsNewInfo(
			isAgency ? whatsNewDataAgency : whatsNewDataCompany,
			lastReadDate,
			isLoading,
		);

		if (!shouldSetLastRead || latestWhatsNewDate === undefined) {
			return;
		}

		if (type === "Agency") {
			if (agencyId) {
				const value: AgencyUserPreferencesDto = {
					LastReadWhatsNewDateAgencyPreference: { Value: latestWhatsNewDate },
				};
				await setPreference(agencyId, AgencyUserPreferenceType.LastReadWhatsNewDateAgency, value);
			}
		} else {
			await setPreferenceWithoutCompany({
				value: { LastReadWhatsNewDatePreference: { Value: latestWhatsNewDate } },
			});
		}
	}, [
		isAgency,
		getPreferenceRequest.data?.Data,
		getPreferenceWithoutCompanyRequest.data?.Data,
		whatsNewDataAgency,
		whatsNewDataCompany,
		agencyId,
		setPreference,
		setPreferenceWithoutCompany,
	]);

	useEffect(() => {
		getWhatsNew()
			.then(notes => {
				setWhatsNewDataAgency(notes.ReleaseNotes.filter(r => r.ReleaseCategories.includes("Agency")));
				setWhatsNewDataCompany(notes.ReleaseNotes.filter(r => r.ReleaseCategories.includes("Company")));
				return;
			})
			.catch(() => {
				setWhatsNewDataAgency([]);
				setWhatsNewDataCompany([]);
			});
	}, [getWhatsNew]);

	useEffect(() => {
		getAgencyUserPreferences();
	}, [getAgencyUserPreferences, agencyId]);

	useEffect(() => {
		const { hasNewNotes } = getWhatsNewInfo(
			whatsNewDataCompany,
			getPreferenceWithoutCompanyRequest.data?.Data?.LastReadWhatsNewDatePreference?.Value,
		);
		// eslint-disable-next-line react-hooks/set-state-in-effect -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
		setHasNewNotesCompany(hasNewNotes);
	}, [
		getPreferenceWithoutCompanyRequest.data?.Data?.LastReadWhatsNewDatePreference?.Value,
		getPreferenceWithoutCompanyRequest.data?.Data?.LastReadWhatsNewDatePreference,
		whatsNewDataCompany,
	]);

	return (
		<ReleaseNotesContext
			value={{
				whatsNewData: isAgency ? whatsNewDataAgency : whatsNewDataCompany,
				hasNewNotesAgency: hasNewNotesAgency,
				hasNewNotesCompany: hasNewNotesCompany,
				noOfUpdates: noOfUpdatesAgency,
				setLastReadPrefernce: setUserPreference,
			}}
		>
			{children}
		</ReleaseNotesContext>
	);
};

export const ReleaseNotesContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const { user } = useTopLevelUser();

	if (!user) {
		return <>{children}</>;
	}

	return <ReleaseNotesContextProviderLoggedIn>{children}</ReleaseNotesContextProviderLoggedIn>;
};
